import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { mutate as swrMutate } from 'swr'
import i18n, { applyLocaleToDocument } from '@/locales'
import { useLocaleStore } from '@/store/localeStore'
import { useSessionUser } from '@/store/authStore'
import { useThemeStore } from '@/store/themeStore'
import { apiUpdatePreferences } from '@/services/AuthService'
import { getLocaleDefinition, type LocaleCode } from '@/configs/locale.config'
import type { UserPreferences } from '@/@types/trackheros'

/**
 * The one place a language change is enacted.
 *
 * Everything that has to move when the language does moves here: i18next, the
 * `lang`/`dir` attributes, dayjs, and the theme store's `direction` (which the
 * side nav, mobile nav and the UI kit's popovers read to decide which side
 * things open towards). Setting the store value alone would translate the text
 * and leave the layout mirrored the wrong way.
 */
const useLocale = () => {
    const locale = useLocaleStore((state) => state.locale)
    const setStoreLocale = useLocaleStore((state) => state.setLocale)
    const setDirection = useThemeStore((state) => state.setDirection)
    const signedIn = useSessionUser((state) => state.session.signedIn)

    const definition = getLocaleDefinition(locale)

    useEffect(() => {
        if (i18n.language !== locale) {
            i18n.changeLanguage(locale)
        }

        const applied = applyLocaleToDocument(locale)
        setDirection(applied.direction)
    }, [locale, setDirection])

    /**
     * Switch language, and record the choice on the account.
     *
     * The store is set first and the request is not awaited: the interface has
     * to turn over on the click, not on a round trip. Every switcher goes
     * through here — the header dropdown as much as the Settings row — so the
     * account always holds whatever the user last picked, which is what lets
     * `LocaleSync` treat the server value as authoritative without fighting a
     * local change.
     */
    const setLocale = useCallback(
        (next: LocaleCode) => {
            setStoreLocale(next)

            if (!signedIn) {
                return
            }

            // Keep Settings' copy of the preferences in step without a refetch;
            // it renders the same choice and would otherwise show the old one.
            swrMutate<UserPreferences>(
                ['preferences'],
                (current) => current && { ...current, language: next },
                { revalidate: false },
            )

            apiUpdatePreferences({ language: next }).catch(() => {
                // Nothing to say to the user: the language they asked for is
                // already on screen. This only affects their *next* device.
            })
        },
        [setStoreLocale, signedIn],
    )

    return {
        locale,
        direction: definition.direction,
        definition,
        setLocale,
    }
}

/**
 * `t` with the app's typing, re-exported so views import one thing.
 *
 * Views should use this rather than `react-i18next` directly — it keeps the
 * import path stable if the i18n library is ever swapped, which is the same
 * reasoning that puts every axios call behind `services/`.
 */
export const useT = () => {
    const { t } = useTranslation()
    return t
}

export default useLocale
